<template>
  <!-- 应聘者面试日历子组件：按面试日期(ijaDateInterview)+面试时间(ijaTimeInterview)排列当天面试 -->
  <!-- 记录条文字格式：招聘(iuName)-应聘者(ijaName)-面试时间(ijaTimeInterview) -->
  <!-- 支持月视图 / 周视图两种时间段；样式 ac- 前缀与列表模式完全隔离 -->
  <div class="ac-calendar" v-loading="loading">
    <!-- 工具栏：视图切换 + 月导航（上一月/月份选择器/下一月/回到今天）或周导航（上一周/下一周/回到本周） -->
    <div class="ac-toolbar">
      <div class="ac-toolbar-left">
        <el-radio-group v-model="calView" size="small">
          <el-radio-button label="month">月视图</el-radio-button>
          <el-radio-button label="week">周视图</el-radio-button>
        </el-radio-group>
        <template v-if="calView === 'month'">
          <el-button size="small" icon="el-icon-arrow-left" @click="prevMonth">上一月</el-button>
          <el-date-picker v-model="calAnchorDate" type="month" size="small" :clearable="false" format="yyyy 年 MM 月"
            placeholder="选择月份" style="width: 150px" @change="onMonthPick"></el-date-picker>
          <el-button size="small" @click="nextMonth">下一月<i class="el-icon-arrow-right el-icon--right"></i></el-button>
          <el-button size="small" type="primary" plain @click="goToday">回到今天</el-button>
        </template>
        <template v-else>
          <el-button size="small" icon="el-icon-arrow-left" @click="prevWeek">上一周</el-button>
          <span class="ac-week-range">{{ weekRangeText }}</span>
          <el-button size="small" @click="nextWeek">下一周<i class="el-icon-arrow-right el-icon--right"></i></el-button>
          <el-button size="small" type="primary" plain @click="goToday">回到本周</el-button>
        </template>
      </div>
      <!-- 统计文案随视图切换：月视图按当月、周视图按当周 -->
      <div class="ac-toolbar-count">{{ countText }}</div>
    </div>
    <!-- 图例：招聘颜色对照（每个招聘官一个颜色）+ 样式说明 -->
    <div class="ac-legend">
      <div class="ac-legend-posts">
        <span v-for="p in recruiterLegend" :key="p.name" class="ac-legend-item">
          <span class="ac-legend-dot" :style="{ background: p.color }"></span>{{ p.name }}
        </span>
      </div>
      <div class="ac-legend-status">
        <span class="ac-legend-tip">颜色 = 招聘 · 虚线 = 面试时间未定</span>
      </div>
    </div>
    <!-- 周表头：周一为每周第一天（与月/周网格列序一致） -->
    <div class="ac-grid-head">
      <div v-for="(w, i) in weekLabels" :key="w" class="ac-head-cell"
        :class="{ 'ac-head-cell--weekend': i >= 5 }">{{ w }}</div>
    </div>
    <!-- 月视图网格：含上下月补位日期（置灰），行高均分撑满一屏 -->
    <div class="ac-grid-body" v-if="calView === 'month'">
      <div v-for="(row, rowIndex) in monthRows" :key="'mrow' + rowIndex" class="ac-grid-row">
        <div v-for="cell in row" :key="cell.key" class="ac-cell"
          :class="{ 'ac-cell--other': !cell.inMonth, 'ac-cell--today': cell.isToday }">
          <div class="ac-cell-date">
            <span class="ac-daynum"
              :class="{ 'is-today': cell.isToday, 'is-weekend': cell.isWeekend }">{{ cell.day }}</span>
            <span v-if="cell.items.length" class="ac-day-count">{{ cell.items.length }}场</span>
          </div>
          <!-- 记录条区：全量渲染，超出格高时格内滚动；悬浮弹出详情 + 查看详情入口 -->
          <div class="ac-cell-items">
            <el-popover v-for="item in cell.items" :key="item.rec.ijaId" placement="top-start" trigger="hover"
              width="300" :open-delay="120" append-to-body>
              <div class="ac-chip" :style="chipStyle(item.rec)" slot="reference">
                <span class="ac-chip-name">{{ chipText(item.rec) }}</span>
              </div>
              <div class="ac-pop">
                <div class="ac-pop-title">
                  <span class="ac-legend-dot" :style="{ background: dotColor(item.rec) }"></span>
                  {{ item.rec.ijaName }}
                  <el-tag size="mini">{{ item.rec.ijaPost || '未指定岗位' }}</el-tag>
                </div>
                <div class="ac-pop-row">招聘：{{ item.rec.iuName || '-' }}</div>
                <div class="ac-pop-row">招聘渠道：{{ item.rec.ijcName || '-' }}</div>
                <div class="ac-pop-row">面试日期：{{ item.rec.ijaDateInterview || '-' }}</div>
                <div class="ac-pop-row">面试时间：{{ item.rec.ijaTimeInterview || '未定' }}</div>
                <div class="ac-pop-row">是否到面：{{ item.rec.ijaFace || '-' }}</div>
                <div class="ac-pop-row">初试反馈：{{ item.rec.ijaFeedbackFirst || '-' }}</div>
                <div class="ac-pop-actions">
                  <el-button type="primary" size="mini" @click="$emit('detail', item.rec.ijaId)">查看详情</el-button>
                </div>
              </div>
            </el-popover>
          </div>
        </div>
      </div>
    </div>
    <!-- 周视图网格：锚点所在周 7 天，单行撑满剩余高度（列宽大、当天面试一屏总览） -->
    <div class="ac-grid-body ac-grid-body--week" v-else>
      <div class="ac-grid-row">
        <div v-for="cell in weekCells" :key="cell.key" class="ac-cell ac-cell--weekcol"
          :class="{ 'ac-cell--today': cell.isToday }">
          <div class="ac-cell-date">
            <span class="ac-daynum ac-daynum--week"
              :class="{ 'is-today': cell.isToday, 'is-weekend': cell.isWeekend }">{{ cell.label }}</span>
            <span v-if="cell.items.length" class="ac-day-count">{{ cell.items.length }}场</span>
          </div>
          <div class="ac-cell-items">
            <el-popover v-for="item in cell.items" :key="item.rec.ijaId" placement="top-start" trigger="hover"
              width="300" :open-delay="120" append-to-body>
              <div class="ac-chip" :style="chipStyle(item.rec)" slot="reference">
                <span class="ac-chip-name">{{ chipText(item.rec) }}</span>
              </div>
              <div class="ac-pop">
                <div class="ac-pop-title">
                  <span class="ac-legend-dot" :style="{ background: dotColor(item.rec) }"></span>
                  {{ item.rec.ijaName }}
                  <el-tag size="mini">{{ item.rec.ijaPost || '未指定岗位' }}</el-tag>
                </div>
                <div class="ac-pop-row">招聘：{{ item.rec.iuName || '-' }}</div>
                <div class="ac-pop-row">招聘渠道：{{ item.rec.ijcName || '-' }}</div>
                <div class="ac-pop-row">面试日期：{{ item.rec.ijaDateInterview || '-' }}</div>
                <div class="ac-pop-row">面试时间：{{ item.rec.ijaTimeInterview || '未定' }}</div>
                <div class="ac-pop-row">是否到面：{{ item.rec.ijaFace || '-' }}</div>
                <div class="ac-pop-row">初试反馈：{{ item.rec.ijaFeedbackFirst || '-' }}</div>
                <div class="ac-pop-actions">
                  <el-button type="primary" size="mini" @click="$emit('detail', item.rec.ijaId)">查看详情</el-button>
                </div>
              </div>
            </el-popover>
          </div>
        </div>
      </div>
    </div>
    <!-- 空态：当前时段（当月 / 当周）无任何面试安排 -->
    <div v-if="calEmpty" class="ac-empty">{{ calView === 'month' ? '本月暂无面试安排' : '本周暂无面试安排' }}</div>
  </div>
</template>

<script>
// 颜色分配：仅按「招聘」(iuName) 分色，每个招聘官一个固定颜色；岗位不参与配色
// 色相按黄金角(137.5°)分布生成 24 槽——相邻分配的颜色色相差最大化，颜色丰富且区分明显
// 卡片样式：淡色底 + 左侧实色条 + 同色系深字（Google Calendar 风格），长时间看不刺眼
const CAL_PALETTE_SIZE = 24;

function calColor(i) {
  const h = Math.round((i * 137.508) % 360);
  return {
    dot: 'hsl(' + h + ', 70%, 45%)',   // 主色：图例圆点
    bar: 'hsl(' + h + ', 70%, 45%)',   // 左侧色条
    bg: 'hsl(' + h + ', 80%, 94%)',    // 淡底
    text: 'hsl(' + h + ', 65%, 30%)',  // 深字（同色系）
  };
}

// Date → yyyy-MM-dd（取字符串前 10 位比较日期，Safari 兼容，不用 toISOString 避免时区偏移）
function fmtDate(d) {
  const m = d.getMonth() + 1;
  const day = d.getDate();
  return (
    d.getFullYear() + '-' + (m < 10 ? '0' + m : m) + '-' + (day < 10 ? '0' + day : day)
  );
}

// ==================== 招聘官稳定配色（localStorage 持久化登记表） ====================
// 要求：同一招聘官在任何月份 / 任何周 / 页面刷新后颜色都保持不变。
// 做法：招聘官名 → 颜色槽 的映射持久化到 localStorage——首次出现的招聘官分配一个
// 当前未被占用的槽并登记，之后永远复用登记的槽，不随数据范围、出现顺序变化。
const CAL_COLOR_STORAGE_KEY = 'recording_calRecruiterColors';

function loadColorRegistry() {
  try {
    return JSON.parse(localStorage.getItem(CAL_COLOR_STORAGE_KEY) || '{}') || {};
  } catch (e) {
    return {};
  }
}

function saveColorRegistry(map) {
  try {
    localStorage.setItem(CAL_COLOR_STORAGE_KEY, JSON.stringify(map));
  } catch (e) {
    /* 存储不可用时静默降级为本次会话内分配 */
  }
}

// 取招聘官的稳定颜色槽：已登记直接用；未登记的挑一个空闲槽登记（色板用尽后按名字哈希回落，保证确定性）
function stableColorSlot(name) {
  const map = loadColorRegistry();
  if (name in map) return map[name];
  const occupied = {};
  Object.keys(map).forEach((k) => { occupied[map[k]] = 1; });
  let slot = 0;
  while (occupied[slot] && slot < CAL_PALETTE_SIZE) slot++;
  if (slot >= CAL_PALETTE_SIZE) {
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
    slot = h % CAL_PALETTE_SIZE;
  }
  map[name] = slot;
  saveColorRegistry(map);
  return slot;
}

export default {
  name: 'ApplicantCalendar',
  props: {
    // 父组件拉取的全量面试记录（仅需含 ijaDateInterview 的记录，父组件已过滤）
    records: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      calView: 'month',            // 当前视图：month=月视图（默认）；week=周视图
      calAnchorDate: new Date(),   // 导航锚点日期：月视图取其年月、周视图取其所在周（周一起始）
      weekLabels: ['一', '二', '三', '四', '五', '六', '日'], // 周一为每周第一天
    };
  },
  watch: {
    // 当前可视时间范围变化（切视图 / 翻月 / 翻周）→ 通知父组件按新范围拉数据
    // 注意：不能加 immediate——created 阶段同步 $emit 会让父组件在 patch 中途改自身
    // data（calLoading），Vue 2.7 下会破坏父组件渲染调度（页面所有弹窗失效）；
    // 初始范围改在 mounted 的 nextTick 里上报（此时父组件首次渲染已完成）
    viewRange(val) {
      this.$emit('range-change', val);
    },
  },
  mounted() {
    // 上报初始可视范围，父组件完成首屏拉取（异步触发，避开父组件渲染周期）
    this.$nextTick(() => {
      this.$emit('range-change', this.viewRange);
    });
  },
  computed: {
    calYear() {
      return this.calAnchorDate.getFullYear();
    },
    calMonth() {
      return this.calAnchorDate.getMonth() + 1;
    },
    // 按「面试日期+面试时间」升序的副本：保证颜色分配顺序与格内展示顺序稳定可复现（不改动 props）
    sortedRecords() {
      const key = (r) => ((r.ijaDateInterview || '') + ' ' + (r.ijaTimeInterview || '')).trim();
      return [...this.records].sort((a, b) => key(a).localeCompare(key(b)));
    },
    // 颜色映射（computed 预计算，渲染时只查表）：ijaId → { style: 记录条样式, dotColor: 圆点色 }
    // 颜色槽来自 localStorage 持久化登记表——同一招聘官跨月 / 跨周 / 刷新页面颜色恒定；
    // 有面试时间：淡底 + 左侧色条 + 深字；时间未定：同淡底 + 虚线边框，一眼可辨
    visualMap() {
      const map = {};
      this.sortedRecords.forEach((rec) => {
        const name = rec.iuName || '未指定';
        const c = calColor(stableColorSlot(name));
        map[rec.ijaId] = rec.ijaTimeInterview
          ? {
            dotColor: c.dot,
            style: {
              background: c.bg,
              color: c.text,
              borderLeft: '3px solid ' + c.bar,
            },
          }
          : {
            dotColor: c.dot,
            style: {
              background: c.bg,
              color: c.text,
              border: '1px dashed ' + c.bar,
            },
          };
      });
      return map;
    },
    // 图例数据：招聘名 + 代表色（取该招聘首个出现记录的颜色），按首次出现顺序
    recruiterLegend() {
      const order = [];
      const colorOf = {};
      this.sortedRecords.forEach((rec) => {
        const name = rec.iuName || '未指定';
        if (!(name in colorOf)) {
          const v = this.visualMap[rec.ijaId];
          colorOf[name] = v ? v.dotColor : '#909399';
          order.push(name);
        }
      });
      return order.map((name) => ({ name: name, color: colorOf[name] }));
    },
    // 取某天的面试记录：以招聘为分组排序——同一招聘的面试排在一起，
    // 组间按该组最早面试时间排（早上有面试的招聘在前），组内按时间升序、时间未定排最后
    itemsOf() {
      return (dateStr) => {
        const items = this.sortedRecords.filter(
          (r) => (r.ijaDateInterview || '').slice(0, 10) === dateStr
        );
        // 第一遍按时间升序：由此确定各招聘组的出现（最早面试时间）顺序
        const byTime = (a, b) =>
          (a.ijaTimeInterview || '99:99').localeCompare(b.ijaTimeInterview || '99:99');
        items.sort(byTime);
        const groupIdx = {}; // 招聘名 → 组序号
        items.forEach((r) => {
          const name = r.iuName || '未指定';
          if (!(name in groupIdx)) groupIdx[name] = Object.keys(groupIdx).length;
        });
        // 第二遍按（组序号, 组内时间）排序，实现分组展示
        items.sort((a, b) => {
          const ga = groupIdx[a.iuName || '未指定'];
          const gb = groupIdx[b.iuName || '未指定'];
          if (ga !== gb) return ga - gb;
          return byTime(a, b);
        });
        return items.map((r) => ({ rec: r }));
      };
    },
    // 月历格子：周一为首列，含上下月补位日期（置灰），行数为当月实际周数（5~6 行）
    monthCells() {
      const y = this.calYear;
      const m = this.calMonth;
      const first = new Date(y, m - 1, 1);
      // getDay(): 0=周日；(getDay()+6)%7 得到周一首列网格下当月 1 号的偏移格数
      const offset = (first.getDay() + 6) % 7;
      const daysInMonth = new Date(y, m, 0).getDate();
      const weeks = Math.ceil((offset + daysInMonth) / 7);
      const todayStr = fmtDate(new Date());
      const cells = [];
      for (let i = 0; i < weeks * 7; i++) {
        // 从当月第一周的周一开始逐格推进日期
        const d = new Date(y, m - 1, 1 - offset + i);
        const dateStr = fmtDate(d);
        cells.push({
          key: dateStr,
          dateStr: dateStr,
          day: d.getDate(),
          inMonth: d.getMonth() === m - 1,
          isToday: dateStr === todayStr,
          isWeekend: i % 7 >= 5,
          items: this.itemsOf(dateStr), // 全量渲染，超出格子高度时由 .ac-cell-items 在格内滚动
        });
      }
      return cells;
    },
    // 月历网格按 7 格一行分组（flex 布局实现，不用 CSS grid，兼容老内核）
    monthRows() {
      const rows = [];
      const cells = this.monthCells;
      for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
      return rows;
    },
    // 锚点所在周的周一
    weekStart() {
      const d = this.calAnchorDate;
      const off = (d.getDay() + 6) % 7; // 周日归上周末尾
      return new Date(d.getFullYear(), d.getMonth(), d.getDate() - off);
    },
    // 当前视图需要拉取的日期范围：月视图=当月 1 号~月末（自然月，非本月补位格不拉数据）；
    // 周视图=当周周一~周日（可能跨月，后端按日期段查询）。父组件用它作 ijaDateInterviewStart/End
    // 范围查询——只拉当前看得到的时段，不做全量
    viewRange() {
      if (this.calView === 'month') {
        const y = this.calYear;
        const m = this.calMonth;
        return {
          start: fmtDate(new Date(y, m - 1, 1)),
          end: fmtDate(new Date(y, m, 0)), // new Date(y, m, 0) = 当月最后一天
        };
      }
      const s = this.weekStart;
      const e = new Date(s.getFullYear(), s.getMonth(), s.getDate() + 6);
      return { start: fmtDate(s), end: fmtDate(e) };
    },
    // 周视图格子：周一 ~ 周日 7 格
    weekCells() {
      const start = this.weekStart;
      const todayStr = fmtDate(new Date());
      const cells = [];
      for (let i = 0; i < 7; i++) {
        const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
        const dateStr = fmtDate(d);
        cells.push({
          key: 'w' + dateStr,
          dateStr: dateStr,
          day: d.getDate(),
          label: (d.getMonth() + 1) + '/' + d.getDate(),
          isToday: dateStr === todayStr,
          isWeekend: i >= 5,
          items: this.itemsOf(dateStr),
        });
      }
      return cells;
    },
    // 周视图日期范围文案：yyyy年 M月d日 - M月d日
    weekRangeText() {
      const s = this.weekStart;
      const e = new Date(s.getFullYear(), s.getMonth(), s.getDate() + 6);
      const f = (d) => d.getMonth() + 1 + '月' + d.getDate() + '日';
      return s.getFullYear() + '年 ' + f(s) + ' - ' + f(e);
    },
    // 工具栏统计文案：月视图统计当月（不含补位格）、周视图统计当周
    countText() {
      if (this.calView === 'month') {
        const n = this.monthCells
          .filter((c) => c.inMonth)
          .reduce((s, c) => s + c.items.length, 0);
        return this.calYear + '年' + this.calMonth + '月 · 当月共 ' + n + ' 场面试';
      }
      const n = this.weekCells.reduce((s, c) => s + c.items.length, 0);
      return this.weekRangeText + ' · 本周共 ' + n + ' 场面试';
    },
    // 空态：当前时段（当月 / 当周）无任何面试
    calEmpty() {
      const cells =
        this.calView === 'month'
          ? this.monthCells.filter((c) => c.inMonth)
          : this.weekCells;
      return cells.every((c) => c.items.length === 0);
    },
  },
  methods: {
    // 记录条文字：招聘-应聘者-面试时间（招聘=招聘官 iuName；时间未定显示占位）
    chipText(rec) {
      return (
        (rec.iuName || '未指定') + '-' + rec.ijaName + '-' + (rec.ijaTimeInterview || '时间未定')
      );
    },
    // 记录条样式：从 visualMap（computed 预计算）查表，渲染时零重复计算
    chipStyle(rec) {
      const v = this.visualMap[rec.ijaId];
      return v ? v.style : {};
    },
    // 圆点色（悬浮卡片用）：同样查表
    dotColor(rec) {
      const v = this.visualMap[rec.ijaId];
      return v ? v.dotColor : '#909399';
    },
    // ===== 月视图导航 =====
    prevMonth() {
      const d = this.calAnchorDate;
      this.calAnchorDate = new Date(d.getFullYear(), d.getMonth() - 1, 1);
    },
    nextMonth() {
      const d = this.calAnchorDate;
      this.calAnchorDate = new Date(d.getFullYear(), d.getMonth() + 1, 1);
    },
    // 月份选择器跳转：归一化到当月 1 号
    onMonthPick(val) {
      if (!val) return;
      this.calAnchorDate = new Date(val.getFullYear(), val.getMonth(), 1);
    },
    // ===== 周视图导航 =====
    prevWeek() {
      const d = this.calAnchorDate;
      this.calAnchorDate = new Date(d.getFullYear(), d.getMonth(), d.getDate() - 7);
    },
    nextWeek() {
      const d = this.calAnchorDate;
      this.calAnchorDate = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 7);
    },
    // 回到今天：月视图回到当月、周视图回到本周（统一重置锚点为今天）
    goToday() {
      const n = new Date();
      this.calAnchorDate = new Date(n.getFullYear(), n.getMonth(), n.getDate());
    },
  },
};
</script>

<style>
/* ==================== 应聘者面试日历样式（ac- 前缀，与列表模式完全隔离） ==================== */
.ac-calendar {
  /* 纵向 flex：工具栏/图例/表头固定高，网格区 flex:1 均分剩余高度，整月一屏呈现 */
  position: relative; /* 空态文案定位基准 */
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 6px 10px 10px;
  overflow-y: auto; /* 兜底：极端小屏仍可滚动 */
}
/* 工具栏 */
.ac-toolbar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.ac-toolbar-left {
  display: flex;
  align-items: center;
}
/* 工具栏内相邻控件统一间距（date-picker 根类名不稳定，用通配相邻选择器） */
.ac-toolbar-left > * + * {
  margin-left: 8px;
}
.ac-week-range {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  white-space: nowrap;
}
.ac-toolbar-count {
  font-size: 12px;
  color: #909399;
  white-space: nowrap;
}
/* 图例：岗位颜色对照 + 样式说明 */
.ac-legend {
  flex-shrink: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 6px 14px;
  margin-bottom: 6px;
  padding: 4px 8px;
  background: #f7f8fa;
  border-radius: 4px;
  font-size: 12px;
  color: #606266;
}
.ac-legend-posts {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
}
.ac-legend-item {
  display: inline-flex;
  align-items: center;
}
.ac-legend-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  margin-right: 4px;
  border-radius: 2px;
  flex-shrink: 0;
}
.ac-legend-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.ac-legend-tip {
  color: #909399;
}
/* 周表头：周一 ~ 周日，周末微红 */
.ac-grid-head {
  flex-shrink: 0;
  display: flex;
}
.ac-head-cell {
  flex: 1;
  padding: 4px 0;
  text-align: center;
  font-weight: 600;
  color: #606266;
  background: #f2f6fc;
  border: 1px solid #ebeef5;
  border-left: 0;
}
.ac-head-cell:first-child {
  border-left: 1px solid #ebeef5;
}
.ac-head-cell--weekend {
  color: #f56c6c;
}
/* 网格：占满剩余高度，行高按剩余空间均分拉伸（月视图整月 / 周视图单行一屏呈现） */
.ac-grid-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0; /* 允许收缩 */
  overflow: hidden;
}
.ac-grid-row {
  display: flex;
  flex: 1;
  min-height: 0; /* 允许行收缩到均分高度，不被格内记录条内容撑开（超出部分由格内滚动消化） */
}
.ac-cell {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column; /* 日期行固定高度 + 记录条区占满剩余并独立滚动 */
  padding: 2px 3px;
  border: 1px solid #ebeef5;
  border-left: 0;
  border-top: 0;
}
.ac-grid-row:first-child .ac-cell {
  border-top: 1px solid #ebeef5;
}
.ac-cell:first-child {
  border-left: 1px solid #ebeef5;
}
.ac-cell--other .ac-daynum {
  color: #c0c4cc; /* 非当月补位日期置灰 */
}
.ac-cell--today {
  background: #f0f7ff; /* 今天整格淡蓝高亮 */
}
.ac-cell-date {
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.ac-daynum {
  display: inline-block;
  min-width: 22px;
  line-height: 22px;
  text-align: center;
  font-size: 13px;
  color: #606266;
}
.ac-daynum--week {
  min-width: 0;
  font-weight: 600;
}
.ac-daynum.is-weekend {
  color: #f56c6c; /* 周末轻微区分 */
}
.ac-daynum.is-today {
  background: #409eff;
  color: #fff;
  border-radius: 11px;
}
/* 当天面试场次角标 */
.ac-day-count {
  font-size: 11px;
  color: #409eff;
  white-space: nowrap;
}
/* 记录条区：超出格子高度时在格子内上下滑动查看（细滚动条，hover 时加深提示可滚） */
.ac-cell-items {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;          /* Firefox */
  scrollbar-color: #c0c4cc transparent;
}
.ac-cell-items::-webkit-scrollbar {
  width: 5px;
}
.ac-cell-items::-webkit-scrollbar-thumb {
  border-radius: 3px;
  background: #dcdfe6;
}
.ac-cell-items:hover::-webkit-scrollbar-thumb {
  background: #909399;
}
.ac-cell-items::-webkit-scrollbar-track {
  background: transparent;
}
/* 记录条：悬浮上浮强调；详情在悬浮卡片（popover）中展示 */
.ac-chip {
  display: flex;
  align-items: center;
  height: 20px;
  margin: 1px 0;
  padding: 0 5px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  overflow: hidden;
  white-space: nowrap;
  transition: box-shadow 0.15s, transform 0.15s;
  box-sizing: border-box;
}
.ac-chip:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  transform: translateY(-1px);
}
.ac-chip-name {
  overflow: hidden;
  text-overflow: ellipsis;
}
/* 悬浮卡片（popover）内容 */
.ac-pop-title {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  font-weight: 600;
  color: #303133;
}
.ac-pop-row {
  font-size: 13px;
  line-height: 26px;
  color: #606266;
}
.ac-pop-actions {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #ebeef5;
}
/* 空态 */
.ac-empty {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 30%;
  text-align: center;
  color: #909399;
  font-size: 14px;
  pointer-events: none;
}
</style>
